import { useEffect, useState } from "react";
import {
  HiCalendar,
  HiChevronDown,
  HiChevronLeft,
  HiChevronRight,
} from "react-icons/hi";
import {
  EXPORT_MONTH_NAMES,
  EXPORT_RELATIVE_PRESETS,
  formatExportDate,
  isMonthAvailable,
  isQuarterAvailable,
  type ExportDateRange,
  type ExportPeriod,
  type ExportQuarter,
} from "../../utils/exportDateRanges";
import { sectionLabelClass, triggerClass, usePopover } from "./usePopover";

type Grain = "month" | "quarter" | "year";

const MIN_YEAR = 1900;

interface Props {
  period: ExportPeriod;
  onChange: (next: ExportPeriod) => void;
  range: ExportDateRange | null;
  dateError: string | null;
  today: string;
}

const chipClass = (active: boolean) =>
  `rounded-md px-2 py-1.5 text-left text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 ${
    active
      ? "bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
      : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
  }`;

const gridButtonClass = (active: boolean) =>
  `rounded-md px-1 py-1.5 text-center text-xs font-medium ring-1 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 ${
    active
      ? "bg-blue-600 text-white ring-blue-600 dark:bg-blue-500 dark:ring-blue-500"
      : "text-gray-700 ring-gray-200 hover:bg-gray-100 disabled:hover:bg-transparent dark:text-gray-200 dark:ring-gray-600 dark:hover:bg-gray-700"
  }`;

const dateInputClass =
  "h-9 min-w-0 flex-1 rounded-lg bg-white px-2 text-xs text-gray-900 ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:ring-gray-600";

function periodYear(period: ExportPeriod, fallback: number): number {
  return "year" in period ? period.year : fallback;
}

export function formatRangeDates(range: ExportDateRange): string {
  return `${formatExportDate(range.dateFrom)} – ${formatExportDate(
    range.dateTo
  )}`;
}

export function ExportPeriodPicker({
  period,
  onChange,
  range,
  dateError,
  today,
}: Props) {
  const popover = usePopover();
  const currentYear = Number(today.slice(0, 4));
  const [grain, setGrain] = useState<Grain>(
    period.kind === "quarter" || period.kind === "year" ? period.kind : "month"
  );
  const [viewYear, setViewYear] = useState(() =>
    periodYear(period, currentYear)
  );

  // Re-anchor the calendar on the selected period every time the picker opens.
  useEffect(() => {
    if (!popover.open) return;
    if (
      period.kind === "month" ||
      period.kind === "quarter" ||
      period.kind === "year"
    )
      setGrain(period.kind);
    setViewYear(
      periodYear(
        period,
        range ? Number(range.dateFrom.slice(0, 4)) : currentYear
      )
    );
    // Only on open: while open the user navigates years freely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popover.open]);

  // Free dates always show the effective range, so switching from a preset to
  // "Eigener Zeitraum" starts from what is currently selected.
  const customFrom =
    period.kind === "custom" ? period.from : range?.dateFrom ?? "";
  const customTo = period.kind === "custom" ? period.to : range?.dateTo ?? "";
  const pick = (next: ExportPeriod) => {
    onChange(next);
    popover.close();
  };

  const label = range ? range.label : "Zeitraum ungültig";

  return (
    <div ref={popover.containerRef} className="relative">
      <button
        ref={popover.triggerRef}
        type="button"
        onClick={popover.toggle}
        className={`${triggerClass} max-w-full ${
          dateError ? "ring-red-400 dark:ring-red-500" : ""
        }`}
        aria-expanded={popover.open}
        aria-haspopup="dialog"
      >
        <HiCalendar
          className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400"
          aria-hidden="true"
        />
        <span className="truncate">{label}</span>
        {range && period.kind !== "custom" && (
          <>
            <span className="hidden text-gray-400 dark:text-gray-500 sm:inline">
              ·
            </span>
            <span className="hidden font-normal text-gray-500 dark:text-gray-400 sm:inline">
              {formatRangeDates(range)}
            </span>
          </>
        )}
        <HiChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${
            popover.open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {popover.open && (
        <div
          role="dialog"
          aria-label="Zeitraum wählen"
          className="absolute left-0 z-30 mt-2 w-[340px] max-w-[calc(100vw-2rem)] rounded-lg bg-white p-3 shadow-lg ring-1 ring-gray-300 dark:bg-gray-800 dark:ring-gray-600"
        >
          <p className={sectionLabelClass}>Schnellauswahl</p>
          <div className="mb-3 grid grid-cols-2 gap-1">
            {EXPORT_RELATIVE_PRESETS.map(({ preset, label: presetLabel }) => {
              const active =
                period.kind === "relative" && period.preset === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  aria-pressed={active}
                  onClick={() => pick({ kind: "relative", preset })}
                  className={chipClass(active)}
                >
                  {presetLabel}
                </button>
              );
            })}
          </div>

          <div className="mb-1.5 flex items-center justify-between gap-2">
            <div
              role="group"
              aria-label="Zeitraumart"
              className="inline-flex rounded-md bg-gray-100 p-0.5 dark:bg-gray-700"
            >
              {(
                [
                  ["month", "Monat"],
                  ["quarter", "Quartal"],
                  ["year", "Jahr"],
                ] as const
              ).map(([value, grainLabel]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={grain === value}
                  onClick={() => setGrain(value)}
                  className={`rounded px-2.5 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    grain === value
                      ? "bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-white"
                      : "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  }`}
                >
                  {grainLabel}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() =>
                  setViewYear((year) => Math.max(MIN_YEAR, year - 1))
                }
                disabled={viewYear <= MIN_YEAR}
                aria-label="Vorheriges Jahr"
                className="rounded p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <HiChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <span
                className="w-12 text-center text-sm font-semibold tabular-nums text-gray-900 dark:text-white"
                aria-live="polite"
              >
                {viewYear}
              </span>
              <button
                type="button"
                onClick={() =>
                  setViewYear((year) => Math.min(currentYear, year + 1))
                }
                disabled={viewYear >= currentYear}
                aria-label="Nächstes Jahr"
                className="rounded p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <HiChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="mb-3">
            {grain === "month" && (
              <div className="grid grid-cols-4 gap-1">
                {EXPORT_MONTH_NAMES.map((name, index) => {
                  const month = index + 1;
                  const available = isMonthAvailable(viewYear, month, today);
                  const active =
                    period.kind === "month" &&
                    period.year === viewYear &&
                    period.month === month;
                  return (
                    <button
                      key={name}
                      type="button"
                      disabled={!available}
                      aria-pressed={active}
                      title={
                        available
                          ? `${name} ${viewYear}`
                          : "Liegt in der Zukunft"
                      }
                      onClick={() =>
                        pick({ kind: "month", year: viewYear, month })
                      }
                      className={gridButtonClass(active)}
                    >
                      {name.slice(0, 3)}
                      <span className="sr-only">
                        {` ${viewYear}`}
                        {available ? "" : ", liegt in der Zukunft"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {grain === "quarter" && (
              <div className="grid grid-cols-4 gap-1">
                {([1, 2, 3, 4] as ExportQuarter[]).map((quarter) => {
                  const available = isQuarterAvailable(
                    quarter,
                    viewYear,
                    today
                  );
                  const active =
                    period.kind === "quarter" &&
                    period.year === viewYear &&
                    period.quarter === quarter;
                  return (
                    <button
                      key={quarter}
                      type="button"
                      disabled={!available}
                      aria-pressed={active}
                      title={
                        available
                          ? `Q${quarter} ${viewYear}`
                          : "Liegt in der Zukunft"
                      }
                      onClick={() =>
                        pick({ kind: "quarter", year: viewYear, quarter })
                      }
                      className={gridButtonClass(active)}
                    >
                      Q{quarter}
                      <span className="sr-only">
                        {` ${viewYear}`}
                        {available ? "" : ", liegt in der Zukunft"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {grain === "year" && (
              <button
                type="button"
                aria-pressed={
                  period.kind === "year" && period.year === viewYear
                }
                onClick={() => pick({ kind: "year", year: viewYear })}
                className={`${gridButtonClass(
                  period.kind === "year" && period.year === viewYear
                )} w-full`}
              >
                Ganzes Jahr {viewYear}
                {viewYear === currentYear ? " (bis heute)" : ""}
              </button>
            )}
          </div>

          <p className={sectionLabelClass}>Eigener Zeitraum</p>
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={customFrom}
              min="1900-01-01"
              max={customTo && customTo < today ? customTo : today}
              onChange={(event) =>
                onChange({
                  kind: "custom",
                  from: event.target.value,
                  to: customTo,
                })
              }
              aria-label="Von"
              className={dateInputClass}
            />
            <span className="text-gray-400" aria-hidden="true">
              –
            </span>
            <input
              type="date"
              value={customTo}
              min={customFrom || "1900-01-01"}
              max={today}
              onChange={(event) =>
                onChange({
                  kind: "custom",
                  from: customFrom,
                  to: event.target.value,
                })
              }
              aria-label="Bis einschließlich"
              className={dateInputClass}
            />
          </div>
          {dateError ? (
            <p
              className="mt-2 text-xs text-red-700 dark:text-red-300"
              role="alert"
            >
              {dateError}
            </p>
          ) : (
            range?.isPartial && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Laufender Zeitraum: endet heute ({formatExportDate(today)}).
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
}
